import {
    UserPlusIcon,
    ImageIcon,
    CalendarIcon,
    BriefcaseIcon,
    PaletteIcon,
    SparklesIcon,
    LightbulbIcon,
    MessageCircleIcon,
    CheckIcon,
    LinkIcon,
    type LucideIcon,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';


const colorClasses = [
    {
        circle: 'from-emerald-500 to-emerald-600',
        bgGradient: 'from-emerald-500/10 to-emerald-500/5',
        border: 'border-emerald-500/20',
        icon: 'text-emerald-600 dark:text-emerald-400',
        line: 'from-emerald-500 to-emerald-400',
    },
    {
        circle: 'from-purple-500 to-purple-600',
        bgGradient: 'from-purple-500/10 to-purple-500/5',
        border: 'border-purple-500/20',
        icon: 'text-purple-600 dark:text-purple-400',
        line: 'from-purple-500 to-purple-400',
    },
    {
        circle: 'from-pink-500 to-pink-600',
        bgGradient: 'from-pink-500/10 to-pink-500/5',
        border: 'border-pink-500/20',
        icon: 'text-pink-600 dark:text-pink-400',
        line: 'from-pink-500 to-pink-400',
    },
];

const benefitIcons: LucideIcon[] = [MessageCircleIcon, CheckIcon, LinkIcon];

const steps = [
    {
        number: "01",
        titleKey: "howItWorks.step01.title",
        descriptionKey: "howItWorks.step01.description",
        problemSolvedKey: "howItWorks.step01.problemSolved",
        icon: UserPlusIcon,
        colorIndex: 0,  // emerald - START
        benefitKeys: [
            "howItWorks.step01.benefit01",
            "howItWorks.step01.benefit02",
            "howItWorks.step01.benefit03",
        ],
    },
    {
        number: "02",
        titleKey: "howItWorks.step02.title",
        descriptionKey: "howItWorks.step02.description",
        problemSolvedKey: "howItWorks.step02.problemSolved",
        icon: ImageIcon,
        colorIndex: 0,  // emerald - START
        benefitKeys: [
            "howItWorks.step02.benefit01",
            "howItWorks.step02.benefit02",
            "howItWorks.step02.benefit03",
        ],
    },
    {
        number: "03",
        titleKey: "howItWorks.step03.title",
        descriptionKey: "howItWorks.step03.description",
        problemSolvedKey: "howItWorks.step03.problemSolved",
        icon: CalendarIcon,
        colorIndex: 1,  // purple - MIDDLE
        benefitKeys: [
            "howItWorks.step03.benefit01",
            "howItWorks.step03.benefit02",
            "howItWorks.step03.benefit03",
        ],
    },
    {
        number: "04",
        titleKey: "howItWorks.step04.title",
        descriptionKey: "howItWorks.step04.description",
        problemSolvedKey: "howItWorks.step04.problemSolved",
        icon: BriefcaseIcon,
        colorIndex: 1,  // purple - MIDDLE
        benefitKeys: [
            "howItWorks.step04.benefit01",
            "howItWorks.step04.benefit02",
            "howItWorks.step04.benefit03",
        ],
    },
    {
        number: "05",
        titleKey: "howItWorks.step05.title",
        descriptionKey: "howItWorks.step05.description",
        problemSolvedKey: "howItWorks.step05.problemSolved",
        icon: PaletteIcon,
        colorIndex: 2,  // pink - END
        benefitKeys: [
            "howItWorks.step05.benefit01",
            "howItWorks.step05.benefit02",
            "howItWorks.step05.benefit03",
        ],
    },
    {
        number: "06",
        titleKey: "howItWorks.step06.title",
        descriptionKey: "howItWorks.step06.description",
        problemSolvedKey: "howItWorks.step06.problemSolved",
        icon: SparklesIcon,
        colorIndex: 2,  // pink - END
        benefitKeys: [
            "howItWorks.step06.benefit01",
            "howItWorks.step06.benefit02",
            "howItWorks.step06.benefit03",
        ],
        isFinal: true,
    },
];

interface BenefitBoxProps {
    icon: LucideIcon;
    textKey: string;
    colorIndex: number;
    delay?: number;
}

function BenefitBox({ icon: Icon, textKey, colorIndex, delay = 0 }: BenefitBoxProps) {
    const { t } = useTranslation('landing')
    const colors = [
        { bg: 'from-emerald-500/10 to-emerald-500/5', border: 'border-emerald-500/20', icon: 'text-emerald-600 dark:text-emerald-400' },
        { bg: 'from-purple-500/10 to-purple-500/5', border: 'border-purple-500/20', icon: 'text-purple-600 dark:text-purple-400' },
        { bg: 'from-pink-500/10 to-pink-500/5', border: 'border-pink-500/20', icon: 'text-pink-600 dark:text-pink-400' },
    ];

    const c = colors[colorIndex];

    return (
        <div
            className={`
        group relative overflow-hidden
        p-4 md:p-5 rounded-xl
        bg-gradient-to-br ${c.bg}
        border ${c.border}
        backdrop-blur-sm
        transition-all duration-300
        hover:shadow-lg hover:-translate-y-1
        animate-fade-in-up lg:ml-0 md:ml-8 sm:ml-0
      `}
            style={{ animationDelay: `${delay}ms` }}
        >
            <div className="relative flex items-start gap-3">
                <div className={`
          w-8 h-8 rounded-lg
          flex items-center justify-center
          bg-background/50 
        `}>
                    <Icon className={`w-4 h-4 ${c.icon}`} />
                </div>
                <p className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors pt-1">
                    {t(textKey)}
                </p>
            </div>
        </div>
    );
}

function StepSection({ step, colors }: { step: typeof steps[0]; colors: typeof colorClasses[0] }) {
    const { t } = useTranslation('landing')
    const sectionRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [benefitsVisible, setBenefitsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Trigger benefit animations after container animation completes
                    setTimeout(() => {
                        setBenefitsVisible(true);
                    }, 700);
                }
            },
            { threshold: 0.2 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={sectionRef}
            className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
                {/* Left Content - 60% */}
                <div className="lg:col-span-3 relative pl-0 md:pl-12">
                    {/* Step number circle - hidden on mobile */}
                    <div
                        className={`
            hidden md:flex absolute left-20 top-0 -translate-x-1/2 w-14 h-14 rounded-full 
            items-center justify-center text-white 
            shadow-lg bg-gradient-to-br ${colors.circle}
            ${step.isFinal ? 'animate-glow-pulse' : ''}
          `}
                    >
                        <step.icon className="w-6 h-6" />
                    </div>

                    {/* Content */}
                    <div className="pt-2 pl-4 ml-8">
                        {/* Icon + Title row */}
                        <div className="flex items-center gap-4 mb-4 ml-8">
                            {/* <div  */}
                            {/*   className={` */}
                            {/*     w-12 h-12 rounded-xl flex items-center justify-center */}
                            {/*     bg-gradient-to-br ${colors.bgGradient} */}
                            {/*   `} */}
                            {/* > */}
                            {/*   <step.icon className="w-6 h-6 text-foreground" /> */}
                            {/* </div> */}
                            <h3 className="text-2xl md:text-3xl font-bold">{t(step.titleKey)}</h3>
                        </div>

                        {/* Description */}
                        <p className="text-muted-foreground text-lg mb-6">
                            {t(step.descriptionKey)}
                        </p>

                        {/* Problem Solved Badge */}
                        <div
                            className={`
              inline-flex items-center gap-2 px-4 py-2 rounded-full
              bg-gradient-to-r ${colors.bgGradient}
              border ${colors.border}
            `}
                        >
                            <LightbulbIcon className={`w-4 h-4 ${colors.icon}`} />
                            <span className="text-sm font-medium text-foreground/80">
                                {t(step.problemSolvedKey)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right Benefits - 40% - Sticky on desktop */}
                <div className="lg:col-span-2">
                    <div className="lg:sticky lg:top-24 flex flex-col gap-4">
                        {step.benefitKeys.map((benefitKey, idx) => (
                            <div className="lg:ml-0 md:ml-8 sm:ml-8">
                                <div
                                    key={idx}
                                    className={`transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} lg:ml-0 md:ml-8 sm:ml-4 md:mr-0 sm:mr-8`}
                                    style={{ transitionDelay: benefitsVisible ? `${idx * 100}ms` : '0ms' }}
                                >
                                    <BenefitBox
                                        icon={benefitIcons[idx]}
                                        textKey={benefitKey}
                                        colorIndex={step.colorIndex}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function HowItWorksTimeline() {
    const { t } = useTranslation('landing')

    return (
        <section id="how-it-works" className="py-20 md:py-28 overflow-hidden bg-gradient-to-br from-pink-500/15 to-emerald-500/25">
            <div className="container px-4 md:px-6">
                {/* Section headline */}
                <div className="text-center mb-16 md:mb-20">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-purple-600 to-pink-600 dark:from-emerald-400 dark:via-purple-400 dark:to-pink-400">
                        {t('howItWorks.title')}
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        {t('howItWorks.subtitle')}
                    </p>
                </div>

                {/* Steps */}
                <div className="relative">
                    {/* Single vertical line that spans all steps */}
                    <div className="hidden md:block absolute left-20 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-500 via-purple-500 to-pink-500 rounded-full opacity-20" />

                    {/* Steps container */}
                    <div className="space-y-16 md:space-y-24">
                        {steps.map((step) => {
                            const colors = colorClasses[step.colorIndex];
                            return (
                                <StepSection key={step.number} step={step} colors={colors} />
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
